import React from 'react'
import {Lang} from 'yougile-lang'
import {ObjUtil, PickKeysByValueType, Util} from 'yougile-util'
import {CompanyPreferences, companyPreferencesFallback} from 'yougile-model'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {Help} from '@components/common/base/help'
import {Switcher} from '@components/common/base/switcher'
import {useGlobalKeyDown} from '@containers/hooks'
import {withUpdaters} from '@containers/updaters'
import {SyncUpdater} from '@containers/updaters/sync-updater'
import {Company} from '@extensions/company'
import {globals} from '@extensions/globals-base'
import {Popup} from '../common/popup/popup'
import {MessageBox} from '../message-box'

interface Props {
  close(): void
}

interface InnerProps {
  companyName: string
  settings: CompanyPreferences
  isAdmin: boolean
  onChange: (settings: CompanyPreferences) => void
}

type SelectSettingKeys = PickKeysByValueType<CompanyPreferences, string>

const SettingSelect = (props: {
  settingName: SelectSettingKeys
  settings: CompanyPreferences
  onChange: (settings: CompanyPreferences) => void
  options: {[key: string]: string}
}) => {
  const {settingName, settings, onChange, options} = props
  const bem = Util.bemFrom('company-edit')
  const change = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({...settings, [settingName]: e.target.value})
  }
  return (
    <select
      className={bem('select')}
      value={settings[settingName] || companyPreferencesFallback[settingName]}
      onChange={change}
    >
      {Object.keys(options).map(v => {
        return (
          <option key={v} value={v}>
            {options[v]}
          </option>
        )
      })}
    </select>
  )
}

type BooleanSettingKeys = PickKeysByValueType<
  CompanyPreferences,
  boolean | undefined
>

const SettingSwitcher = (props: {
  settingName: BooleanSettingKeys
  settings: CompanyPreferences
  onChange: (settings: CompanyPreferences) => void
  defaultValue?: boolean
}) => {
  const {settingName, settings, onChange, defaultValue = false} = props
  const value = settings[settingName] ?? defaultValue
  const change = () => {
    const newValue = !value
    if (settingName === 'useOldId') {
      if (newValue) {
        void Tracking.send(TrackingEvent.companySettingsUseOldIdOn)
      } else {
        void Tracking.send(TrackingEvent.companySettingsUseOldIdOff)
      }
    }
    if (settingName === 'useNewGantt') {
      void Tracking.send(
        newValue
          ? TrackingEvent.companySettingsUseNewGanttOn
          : TrackingEvent.companySettingsUseNewGanttOff
      )
    }
    return onChange({...settings, [settingName]: newValue})
  }
  return <Switcher active={value} onClick={change} />
}

// eslint-disable-next-line max-lines-per-function
const CompanySettingsPopupBase = (props: Props & InnerProps) => {
  const {close, onChange, settings, isAdmin} = props
  const bem = Util.bemFrom('company-edit')
  const [currSettings, setSett] = React.useState<CompanyPreferences>(settings)
  useGlobalKeyDown({
    escape: () => {
      close()
      return true
    }
  })
  const setCurrSettings = (v: CompanyPreferences, force = false) => {
    if (!force && !isAdmin) {
      MessageBox.warn(Lang.msg.companies.companyEditWarn)
      return
    }
    setSett(v)
  }
  React.useEffect(() => {
    setCurrSettings(settings, true)
  }, [settings])
  const save = () => {
    if (isAdmin) {
      onChange(currSettings)
    }
    close()
  }
  const footer = (
    <div className={bem('footer')}>
      <div
        className={bem('save', {
          disabled: ObjUtil.deepEqual(currSettings, settings, false)
        })}
        onClick={save}
      >
        {Lang.msg.save}
      </div>
    </div>
  )
  const optsData = Lang.msg.companies

  return (
    <Popup
      close={close}
      title={
        <div className="flex items-center gap-8">
          <span>{Lang.msg.companies.settings}</span>
          <Help name="companySettings" iconClassName="-mx-4 !w-24 !h-28" />
        </div>
      }
      width={616}
      autoHeight={false}
      footer={footer}
      className={bem({subpage: false})}
    >
      <div className={bem('main')}>
        {/* <div className={bem('explain')}>
              {Lang.msg.companies.explainCompanyEdit(companyName)}
            </div> */}
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.whoCanAddProjects}
          </div>
          <SettingSelect
            settingName={'createProjects'}
            settings={currSettings}
            onChange={setCurrSettings}
            options={optsData.whoCanAddProjectsOpts}
          />
        </div>
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.whoCanSeePage(Lang.msg.reportsTitle)}
          </div>
          <SettingSelect
            settingName={'seeStatsPage'}
            settings={currSettings}
            onChange={setCurrSettings}
            options={optsData.whoCanSeePageOpts}
          />
        </div>
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.whoCanEditPosition}
          </div>
          <SettingSelect
            settingName={'editPosition'}
            settings={currSettings}
            onChange={setCurrSettings}
            options={optsData.whoCanEditPositionOpts}
          />
        </div>
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.whoCanCreateGroupChat}
          </div>
          <SettingSelect
            settingName={'createGroupChat'}
            settings={currSettings}
            onChange={setCurrSettings}
            options={optsData.whoCanCreateGroupChatOpts}
          />
        </div>
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.whoCanSeeEmails}
          </div>
          <SettingSelect
            settingName={'seeEmails'}
            settings={currSettings}
            onChange={setCurrSettings}
            options={optsData.whoCanSeeEmailsOpts}
          />
        </div>
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.whoCanEditOwnEmail}
          </div>
          <SettingSelect
            settingName={'editOwnEmail'}
            settings={currSettings}
            onChange={setCurrSettings}
            options={optsData.whoCanEditOwnEmailOpts}
          />
        </div>
        {!globals.standalone && (
          <div className={bem('item')}>
            <div className={bem('title')}>
              {Lang.msg.companies.whoCanSeePage(Lang.msg.billingPageTitle)}
            </div>
            <SettingSelect
              settingName={'seeLicensePage'}
              settings={currSettings}
              onChange={setCurrSettings}
              options={optsData.whoCanSeePageOpts}
            />
          </div>
        )}
        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.setAutoCoverForNewBoards}
            <div className="relative inline-block h-0 ml-6">
              <Help
                name="boardSettingsAutoTaskCover"
                iconClassName="absolute -bottom-[3px]"
              />
            </div>
          </div>
          <SettingSwitcher
            settingName={'setAutoTaskCoverForNewBoards'}
            settings={currSettings}
            onChange={setCurrSettings}
            defaultValue={false}
          />
        </div>

        <div className={bem('item')}>
          <div className={bem('title')}>{Lang.msg.companies.setUseOldId}</div>
          <SettingSwitcher
            settingName={'useOldId'}
            settings={currSettings}
            onChange={setCurrSettings}
            defaultValue={false}
          />
        </div>

        <div className={bem('item')}>
          <div className={bem('title')}>
            {Lang.msg.companies.setUseNewGantt}
          </div>
          <SettingSwitcher
            settingName={'useNewGantt'}
            settings={currSettings}
            onChange={setCurrSettings}
            defaultValue={true}
          />
        </div>
      </div>
    </Popup>
  )
}

export const CompanySettingsPopup = withUpdaters(
  SyncUpdater({filter: '*', companyType: 'current', debounceTime: 500})
)((props: Props) => {
  const companyId = globals.getCompanyId()
  const companyName = Company.get(companyId).name
  const settings = CompanyPreferencesClient.get(companyId)
  const onChange = (newSett: CompanyPreferences) =>
    CompanyPreferencesClient.set(companyId, newSett)

  const isAdmin = PermissionsChecker.isAdmin()
  return (
    <CompanySettingsPopupBase
      {...props}
      settings={settings}
      onChange={onChange}
      companyName={companyName}
      isAdmin={isAdmin}
    />
  )
})
